import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Menu, Phone, MapPin, Mail, Star, Scissors, ChevronRight, MessageCircle, Instagram, Facebook, Calendar, Users, Accessibility, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import ChatWidget from '@/components/ChatWidget';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/cd5s20vv_isalon2022-01.png";
const HERO_MOBILE_URL = "https://customer-assets.emergentagent.com/job_d455e440-05ee-49db-aef7-f865d8ee5622/artifacts/4wjrehmk_mobile-salon.jpg";
const GOOGLE_REVIEWS_URL = "https://www.google.com/search?q=isalon+southport&rlz=1C1CHBF_en-GBGB926GB926&oq=isalon+&gs_lcrp=EgZjaHJvbWUqBggAEEUYOzIGCAAQRRg7MhAIARAuGK8BGMcBGIAEGI4FMgYIAhBFGDsyBggDEEUYOTINCAQQLhivARjHARiABDIHCAUQABiABDIGCAYQRRg8MgYIBxBFGDzSAQgyMjMwajBqNKgCALACAQ&sourceid=chrome&ie=UTF-8#";
const FACEBOOK_REVIEWS_URL = "https://www.facebook.com/isalonuk/reviews/?id=100056815094879&sk=reviews";

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [startBookingOnOpen, setStartBookingOnOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.fade-in-section').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Function to open chat for booking - starts booking flow immediately
  const openChatForBooking = () => {
    setStartBookingOnOpen(true);
    setChatOpen(true);
  };

  // Reset startBookingOnOpen when chat closes
  const handleChatOpenChange = (open) => {
    setChatOpen(open);
    if (!open) {
      setStartBookingOnOpen(false);
    }
  };

  const services = [
    {
      title: "Cut & Finish",
      price: "from £45",
      description: "Expert precision cuts tailored to your style",
      image: "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/anz7rtuf_cutting.jpg"
    },
    {
      title: "Colouring",
      price: "from £60",
      description: "Vibrant colours and expert blending techniques",
      image: "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/5ez0h34p_coloring.jpg"
    },
    {
      title: "Proms & Events",
      price: "from £40",
      description: "Look stunning for your special occasions",
      image: "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/tyr6duld_washing.jpg"
    },
    {
      title: "Blow Dries",
      price: "from £20",
      description: "Beautiful blow dries for any occasion",
      image: "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/uyo9aq88_blowdry.jpg"
    }
  ];

  const testimonials = [
    { 
      name: "Victoria Catton", 
      text: "Emma is always lovely and knows my hair really well!", 
      rating: 5 
    },
    { 
      name: "Jennifer Swain", 
      text: "The best hairdressers. Lorraine is supremely talented with both colour and cut. I've never had so many compliments about my hair!", 
      rating: 5 
    },
    { 
      name: "Katie S", 
      text: "Our hair was styled to perfection with patience and care. Charlotte is amazing - she listens and delivers every time.", 
      rating: 5 
    },
    { 
      name: "Ashley Browne", 
      text: "Been going to iSalon for over 4 years now and wouldn't trust anyone else with my hair. The staff are great!", 
      rating: 5 
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="homepage">
      {/* Navigation */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-black/95 backdrop-blur-md shadow-md border-b border-white/10' : 'bg-transparent'
        }`}
        data-testid="main-nav"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center" data-testid="logo">
              <img src={LOGO_URL} alt="iSalon UK" className="h-10 md:h-12 w-auto" />
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors" data-testid="nav-home">
                Home
              </Link>
              <Link to="/about" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors" data-testid="nav-about">
                About
              </Link>
              <Link to="/prices" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors" data-testid="nav-prices">
                Price List
              </Link>
              <Link to="/opportunities" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors" data-testid="nav-opportunities">
                Join Us
              </Link>
              <Button onClick={openChatForBooking} data-testid="nav-book-btn" className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full px-6 btn-shine">
                Book Now
              </Button>
            </div>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <button className="p-2 rounded-lg text-white" data-testid="mobile-menu-btn">
                  <Menu size={40} strokeWidth={2.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm bg-black border-l border-white/10 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-white/10">
                    <img src={LOGO_URL} alt="iSalon UK" className="h-10 w-auto mb-4" />
                    <SheetClose asChild>
                      <Button onClick={openChatForBooking} className="w-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full py-5 text-lg" data-testid="mobile-book-btn">
                        <Calendar className="mr-2" size={20} />
                        Book Now
                      </Button>
                    </SheetClose>
                  </div>
                  <div className="flex-1 p-6 overflow-y-auto">
                    <nav className="flex flex-col gap-4">
                      <SheetClose asChild>
                        <Link to="/" className="flex items-center justify-between py-3 px-4 rounded-xl text-left font-sans text-lg font-medium text-white hover:bg-white/5 transition-colors">
                          Home
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/about" className="flex items-center justify-between py-3 px-4 rounded-xl text-left font-sans text-lg font-medium text-white hover:bg-white/5 transition-colors">
                          About
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/prices" className="flex items-center justify-between py-3 px-4 rounded-xl text-left font-sans text-lg font-medium text-white hover:bg-white/5 transition-colors">
                          Price List
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/opportunities" className="flex items-center justify-between py-3 px-4 rounded-xl text-left font-sans text-lg font-medium text-white hover:bg-white/5 transition-colors">
                          Join Us
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <button onClick={() => scrollToSection('contact')} className="flex items-center justify-between py-3 px-4 rounded-xl text-left font-sans text-lg font-medium text-white hover:bg-white/5 transition-colors w-full">
                          Contact Us
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </button>
                      </SheetClose>
                    </nav>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* SEO Meta Tags */}
      <Helmet>
        <title>iSalon UK | Premier Hair Salon in Southport | Book Online</title>
        <meta name="description" content="iSalon UK is Southport's premier hair salon offering expert cuts, colours, and styling. Wheelchair accessible with free WiFi. Book your appointment online today!" />
        <meta name="keywords" content="hair salon southport, hairdresser southport, hair stylist, cut and colour, hair extensions, wheelchair accessible salon" />
        <link rel="canonical" href="https://www.isalon.uk" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[100svh] min-h-[600px] flex items-start md:items-center pt-24 md:pt-0" data-testid="hero-section">
        <div className="absolute inset-0">
          {/* Mobile Hero Image */}
          <img src={HERO_MOBILE_URL} alt="iSalon UK" className="md:hidden w-full h-full object-cover" />
          {/* Desktop Hero Image */}
          <img src="https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/rdmn0hwe_rentachair.jpg" alt="iSalon UK Interior" className="hidden md:block w-full h-full object-cover" />
          <div className="absolute inset-0 hero-gradient"></div>
        </div>

        <div className="relative z-10 w-full px-6 md:px-12 mt-8 md:mt-0">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-xl text-center md:text-left mx-auto md:mx-0">
              <p className="text-isalonuk-accent font-sans text-sm md:text-base font-medium tracking-widest uppercase mb-4 animate-fadeInUp">
                Southport's Premier Salon
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6 animate-fadeInUp stagger-1">
                Your Number One Hair Salon
              </h1>
              <p className="text-white/80 font-sans text-base md:text-lg leading-relaxed mb-8 animate-fadeInUp stagger-2">
                Experience · Knowledge · Skill · Quality
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp stagger-3 justify-center md:justify-start">
                <Button onClick={openChatForBooking} size="lg" className="w-full sm:w-auto bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-semibold rounded-full px-8 py-6 text-base btn-shine" data-testid="hero-book-btn">
                  <Calendar className="mr-2" size={18} />
                  Book Appointment
                </Button>
                <Link to="/prices">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/40 text-white hover:bg-white/10 font-sans font-medium rounded-full px-8 py-6 text-base" data-testid="hero-services-btn">
                    View Prices
                    <ChevronRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Review Badges - Mobile - Centered */}
        <div className="absolute bottom-24 left-4 right-4 md:hidden animate-fadeIn stagger-4">
          <div className="flex gap-3 justify-center">
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="flex-1 max-w-[180px] bg-black/80 backdrop-blur-md border border-white/10 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={10} fill="#97A301" className="text-isalonuk-accent" />
                    ))}
                  </div>
                  <p className="text-white/60 font-sans text-[10px]">95+ Google Reviews</p>
                </div>
              </div>
            </a>
            <a href={FACEBOOK_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="flex-1 max-w-[180px] bg-black/80 backdrop-blur-md border border-white/10 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 w-10 h-10 bg-[#1877F2] rounded-lg flex items-center justify-center">
                  <Facebook size={20} className="text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={10} fill="#97A301" className="text-isalonuk-accent" />
                    ))}
                  </div>
                  <p className="text-white/60 font-sans text-[10px]">50+ Facebook Reviews</p>
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Review Badges - Desktop - Centered */}
        <div className="hidden md:flex absolute bottom-12 left-1/2 -translate-x-1/2 gap-4 animate-fadeIn stagger-4">
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="bg-black/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 hover:border-isalonuk-accent/50 transition-colors">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <img src="https://www.google.com/favicon.ico" alt="Google" className="w-8 h-8" />
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="#97A301" className="text-isalonuk-accent" />
                  ))}
                  <span className="text-white font-sans font-bold text-lg ml-2">4.9</span>
                </div>
                <p className="text-white/60 font-sans text-sm">Based on 95+ Google Reviews</p>
              </div>
            </div>
          </a>
          <a href={FACEBOOK_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="bg-black/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 hover:border-isalonuk-accent/50 transition-colors">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-14 h-14 bg-[#1877F2] rounded-xl flex items-center justify-center shadow-sm">
                <Facebook size={28} className="text-white" />
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="#97A301" className="text-isalonuk-accent" />
                  ))}
                  <span className="text-white font-sans font-bold text-lg ml-2">5.0</span>
                </div>
                <p className="text-white/60 font-sans text-sm">Based on 50+ Facebook Reviews</p>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-24 px-6 md:px-12 bg-black" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16 fade-in-section">
            <p className="text-isalonuk-accent font-sans text-sm font-medium tracking-widest uppercase mb-3">What We Offer</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-bold">Our Services</h2>
          </div>

          <div className="md:hidden overflow-x-auto no-scrollbar scroll-snap-x -mx-6 px-6">
            <div className="flex gap-4 pb-4" style={{ width: 'max-content' }}>
              {services.map((service, index) => (
                <div key={index} className="scroll-snap-item w-[280px] flex-shrink-0 fade-in-section" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Card className="service-card overflow-hidden border border-white/10 shadow-lg rounded-2xl bg-zinc-900 h-full">
                    <div className="img-zoom aspect-[4/3]">
                      <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                    </div>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-heading text-xl font-semibold text-white">{service.title}</h3>
                        <span className="text-isalonuk-accent font-sans font-bold text-sm bg-isalonuk-accent/20 px-3 py-1 rounded-full">{service.price}</span>
                      </div>
                      <p className="text-white/60 font-sans text-sm leading-relaxed">{service.description}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
            {/* Scroll indicator for mobile */}
            <div className="flex items-center justify-center gap-2 mt-4 text-white/50">
              <ChevronRight size={16} className="animate-pulse" />
              <span className="text-xs font-sans">Swipe to see more</span>
              <ChevronRight size={16} className="animate-pulse" />
            </div>
          </div>

          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div key={index} className="fade-in-section" style={{ animationDelay: `${index * 0.1}s` }}>
                <Card className="service-card overflow-hidden border border-white/10 shadow-lg rounded-2xl bg-zinc-900 h-full" data-testid={`service-card-${index}`}>
                  <div className="img-zoom aspect-[4/3]">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-heading text-xl font-semibold text-white">{service.title}</h3>
                      <span className="text-isalonuk-accent font-sans font-bold text-sm bg-isalonuk-accent/20 px-3 py-1 rounded-full">{service.price}</span>
                    </div>
                    <p className="text-white/60 font-sans text-sm leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 md:mt-12 fade-in-section">
            <Link to="/prices">
              <Button size="lg" className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full px-8" data-testid="services-book-btn">
                View Full Price List
                <ChevronRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section id="about" className="py-16 md:py-24 bg-zinc-900 relative overflow-hidden" data-testid="about-section">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="fade-in-section">
              <p className="text-isalonuk-accent font-sans text-sm font-medium tracking-widest uppercase mb-3">Since 2012</p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-6">Welcome to iSalon</h2>
              <p className="text-white/70 font-sans text-base md:text-lg leading-relaxed mb-6">
                iSalon is a modern hairdressers in Southport town centre. Our hair salon has been trading since 2012 and we have built up a solid reputation for being one of the best hair salons in Southport.
              </p>
              <p className="text-white/70 font-sans text-base md:text-lg leading-relaxed mb-8">
                Our hair stylists have many years experience in the hairdressing industry. We are trusted and recommended by local hotels in the area for visitors from out of town.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="text-center p-4 rounded-2xl bg-black/50 border border-white/10">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-isalonuk-accent">12+</p>
                  <p className="text-white/60 font-sans text-xs mt-1">Years Experience</p>
                </div>
                <div className="text-center p-4 rounded-2xl bg-black/50 border border-white/10">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-isalonuk-accent">12</p>
                  <p className="text-white/60 font-sans text-xs mt-1">Styling Sections</p>
                </div>
                <div className="text-center p-4 rounded-2xl bg-black/50 border border-white/10">
                  <Accessibility className="text-isalonuk-accent mx-auto mb-1" size={28} />
                  <p className="text-white/60 font-sans text-xs">Wheelchair Accessible</p>
                </div>
                <div className="text-center p-4 rounded-2xl bg-black/50 border border-white/10">
                  <Wifi className="text-isalonuk-accent mx-auto mb-1" size={28} />
                  <p className="text-white/60 font-sans text-xs">Free Wi-Fi</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://wa.me/441704500872" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full px-8" data-testid="about-whatsapp-btn">
                    <MessageCircle className="mr-2" size={18} />
                    Chat on WhatsApp
                  </Button>
                </a>
                <Link to="/about">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-sans font-medium rounded-full px-8" data-testid="about-more-btn">
                    Learn More
                    <ChevronRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="fade-in-section hidden md:block">
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                  <img src="https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/8roxryao_about.jpg" alt="iSalon Interior" className="w-full aspect-[4/5] object-cover" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-zinc-900 border border-white/10 rounded-2xl p-6 shadow-xl max-w-[200px]">
                  <Scissors className="text-isalonuk-accent mb-3" size={32} />
                  <p className="font-heading text-lg font-semibold text-white">Expert Stylists</p>
                  <p className="text-white/60 font-sans text-sm">Emma, Charlotte, Lorraine & more</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chair Rental CTA */}
      <section className="py-12 md:py-16 bg-isalonuk-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-black mb-2">
                Looking to Rent a Chair or Beauty Room?
              </h3>
              <p className="text-black/70 font-sans">Join our busy, friendly salon with 12 sections available</p>
            </div>
            <Link to="/opportunities">
              <Button size="lg" className="bg-black hover:bg-zinc-900 text-white font-sans font-semibold rounded-full px-8" data-testid="join-cta-btn">
                <Users className="mr-2" size={18} />
                Chair Rental Details
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="reviews" className="py-16 md:py-24 px-6 md:px-12 bg-black" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16 fade-in-section">
            <p className="text-isalonuk-accent font-sans text-sm font-medium tracking-widest uppercase mb-3">Testimonials</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-4">Trusted by Southport</h2>
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-80 transition-opacity">
              <div className="flex items-center justify-center gap-2 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={24} fill="#97A301" className="text-isalonuk-accent" />
                ))}
              </div>
              <p className="text-white/60 font-sans">4.9 out of 5 based on 95+ Google Reviews</p>
            </a>
          </div>

          <div className="md:hidden overflow-x-auto no-scrollbar scroll-snap-x -mx-6 px-6">
            <div className="flex gap-4 pb-4" style={{ width: 'max-content' }}>
              {testimonials.map((testimonial, index) => (
                <div key={index} className="scroll-snap-item w-[300px] flex-shrink-0">
                  <Card className="testimonial-card h-full border border-white/10 shadow-lg rounded-2xl bg-zinc-900 p-6">
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} size={16} fill="#97A301" className="text-isalonuk-accent" />
                      ))}
                    </div>
                    <p className="text-white/80 font-sans text-base leading-relaxed mb-4 italic">"{testimonial.text}"</p>
                    <p className="font-sans font-semibold text-isalonuk-accent">{testimonial.name}</p>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="fade-in-section" style={{ animationDelay: `${index * 0.1}s` }}>
                <Card className="testimonial-card h-full border border-white/10 shadow-lg rounded-2xl bg-zinc-900 p-6" data-testid={`testimonial-card-${index}`}>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} fill="#97A301" className="text-isalonuk-accent" />
                    ))}
                  </div>
                  <p className="text-white/80 font-sans text-base leading-relaxed mb-4 italic">"{testimonial.text}"</p>
                  <p className="font-sans font-semibold text-isalonuk-accent">{testimonial.name}</p>
                </Card>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 md:mt-12 fade-in-section">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 font-sans font-medium rounded-full px-8" data-testid="reviews-google-btn">
                  See All Google Reviews
                  <ChevronRight className="ml-2" size={18} />
                </Button>
              </a>
              <a href={FACEBOOK_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 font-sans font-medium rounded-full px-8" data-testid="reviews-facebook-btn">
                  <Facebook className="mr-2" size={18} />
                  Facebook Reviews
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 relative overflow-hidden" data-testid="contact-section">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/rdmn0hwe_rentachair.jpg" 
            alt="iSalon Interior" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-12 md:mb-16 fade-in-section">
            <p className="text-isalonuk-accent font-sans text-sm font-medium tracking-widest uppercase mb-3">Get In Touch</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-bold">Visit Our Salon</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6 md:gap-8">
            <Card className="fade-in-section border border-white/20 shadow-lg rounded-2xl bg-black/60 backdrop-blur-sm p-6 md:p-8 text-center" data-testid="contact-location-card">
              <div className="w-14 h-14 bg-isalonuk-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="text-isalonuk-accent" size={24} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Location</h3>
              <p className="text-white/60 font-sans text-sm leading-relaxed">89B London St, Southport, PR9 0TX</p>
              <a href="https://maps.google.com/?q=89B+London+Street+Southport+PR9+0TX" target="_blank" rel="noopener noreferrer" className="inline-block mt-4 text-isalonuk-accent font-sans font-medium text-sm hover:underline">
                Get Directions →
              </a>
            </Card>

            <Card className="fade-in-section border border-white/20 shadow-lg rounded-2xl bg-black/60 backdrop-blur-sm p-6 md:p-8 text-center" data-testid="contact-phone-card">
              <div className="w-14 h-14 bg-isalonuk-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="text-isalonuk-accent" size={24} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Call Us</h3>
              <p className="text-white/60 font-sans text-sm leading-relaxed mb-2">For appointments & enquiries</p>
              <a href="tel:01704500872" className="text-isalonuk-accent font-sans font-bold text-lg hover:underline">01704 500872</a>
            </Card>

            <Card className="fade-in-section border border-white/20 shadow-lg rounded-2xl bg-black/60 backdrop-blur-sm p-6 md:p-8 text-center" data-testid="contact-email-card">
              <div className="w-14 h-14 bg-isalonuk-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-isalonuk-accent" size={24} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Email Us</h3>
              <p className="text-white/60 font-sans text-sm leading-relaxed mb-2">For bookings & questions</p>
              <a href="mailto:bookings@isalon.uk" className="text-isalonuk-accent font-sans font-medium text-sm hover:underline break-all">bookings@isalon.uk</a>
            </Card>

            <Card 
              className="fade-in-section border border-isalonuk-accent/50 shadow-lg rounded-2xl bg-black/60 backdrop-blur-sm p-6 md:p-8 text-center cursor-pointer hover:border-isalonuk-accent transition-colors" 
              data-testid="contact-appointment-card"
              onClick={openChatForBooking}
            >
              <div className="w-14 h-14 bg-isalonuk-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="text-isalonuk-accent" size={24} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Request Appointment</h3>
              <p className="text-white/60 font-sans text-sm leading-relaxed mb-2">Chat with us to book</p>
              <span className="text-isalonuk-accent font-sans font-medium text-sm">Click Here →</span>
            </Card>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-6 fade-in-section">
            <div className="flex items-center gap-2 text-white/60">
              <Accessibility size={20} className="text-isalonuk-accent" />
              <span className="font-sans text-sm">Wheelchair Accessible</span>
            </div>
            <div className="flex items-center gap-2 text-white/60">
              <MapPin size={20} className="text-isalonuk-accent" />
              <span className="font-sans text-sm">Disabled Parking Available</span>
            </div>
          </div>

          <div className="mt-12 md:mt-16 fade-in-section">
            <div className="bg-gradient-to-r from-isalonuk-accent/20 to-isalonuk-accent/10 border border-isalonuk-accent/30 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Ready to Book an Appointment?</h3>
                <p className="text-white/70 font-sans mb-6 max-w-lg mx-auto">Request your appointment today and experience Southport's premier hair salon.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button onClick={openChatForBooking} size="lg" className="w-full sm:w-auto bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-semibold rounded-full px-8 btn-shine" data-testid="cta-book-btn">
                    <Calendar className="mr-2" size={18} />
                    Book Online Now
                  </Button>
                  <a href="https://wa.me/441704500872" target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/40 text-white hover:bg-white/10 font-sans font-medium rounded-full px-8" data-testid="cta-whatsapp-btn">
                      <MessageCircle className="mr-2" size={18} />
                      WhatsApp Us
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 py-12 md:py-16" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-4">
                <img src={LOGO_URL} alt="iSalon UK" className="h-10 w-auto" />
              </div>
              <p className="text-white/60 font-sans text-sm leading-relaxed mb-1">Established in 2012</p>
              <p className="text-white/60 font-sans text-sm leading-relaxed mb-4">Experience · Knowledge · Skill · Quality</p>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/226899317381783" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors" aria-label="Facebook">
                  <Facebook size={18} className="text-white" />
                </a>
                <a href="https://www.instagram.com/isalonuk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors" aria-label="Instagram">
                  <Instagram size={18} className="text-white" />
                </a>
                <a href="https://twitter.com/isalonuk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors" aria-label="Twitter">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-sans font-semibold text-white mb-4">Quick Links</h4>
              <nav className="flex flex-col gap-2">
                <Link to="/about" className="text-white/60 font-sans text-sm text-left hover:text-isalonuk-accent transition-colors">About Us</Link>
                <Link to="/prices" className="text-white/60 font-sans text-sm text-left hover:text-isalonuk-accent transition-colors">Price List</Link>
                <Link to="/opportunities" className="text-white/60 font-sans text-sm text-left hover:text-isalonuk-accent transition-colors">Join Us</Link>
                <button onClick={() => scrollToSection('contact')} className="text-white/60 font-sans text-sm text-left hover:text-isalonuk-accent transition-colors">Contact</button>
              </nav>
            </div>

            <div>
              <h4 className="font-sans font-semibold text-white mb-4">Contact</h4>
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2">
                  <MapPin size={16} className="text-isalonuk-accent mt-1 flex-shrink-0" />
                  <span className="text-white/60 font-sans text-sm whitespace-nowrap">89B London St, Southport, PR9 0TX</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <a href="tel:01704500872" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">01704 500872</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <a href="mailto:bookings@isalon.uk" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent break-all">bookings@isalon.uk</a>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <button onClick={openChatForBooking} className="text-white/60 font-sans text-sm hover:text-isalonuk-accent text-left">Request Appointment</button>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <a href="https://wa.me/441704500872" target="_blank" rel="noopener noreferrer" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">WhatsApp Us</a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="text-white/40 font-sans text-sm">© {new Date().getFullYear()} iSalon UK. All rights reserved.</p>
              <p className="text-white/40 font-sans text-xs leading-relaxed max-w-lg">
                iSalon UK is a trading name of Scissors Limited, a company registered in England and Wales with company registration number 11875206.
              </p>
              <div className="flex flex-col items-center gap-1">
                <p className="text-isalonuk-accent font-sans text-sm font-medium">
                  Website designed and built by <a href="https://www.barton.uk" target="_blank" rel="noopener noreferrer" className="hover:underline">www.barton.uk</a>
                </p>
                <Link to="/privacy-policy" className="text-white/40 font-sans text-xs hover:text-isalonuk-accent transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky Bottom Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 mobile-bottom-bar bg-black border-t border-white/10 p-4" data-testid="mobile-sticky-bar">
        <div className="flex gap-3">
          <a href="tel:01704500872" className="flex-1">
            <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white/10 font-sans font-medium rounded-full py-5" data-testid="sticky-call-btn">
              <Phone className="mr-2" size={18} />
              Call
            </Button>
          </a>
          <Button onClick={openChatForBooking} className="flex-[2] w-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-semibold rounded-full py-5 btn-shine" data-testid="sticky-book-btn">
            <Calendar className="mr-2" size={18} />
            Book Now
          </Button>
        </div>
      </div>

      <div className="md:hidden h-20"></div>

      {/* AI Chat Widget - now handles all bookings - auto-opens after 2 seconds on homepage only */}
      <ChatWidget 
        isOpen={chatOpen} 
        onOpenChange={handleChatOpenChange} 
        startBookingOnOpen={startBookingOnOpen}
        autoOpen={true}
        isHomePage={true}
      />
    </div>
  );
};

export default HomePage;
